/**
 * Hotel-operations domain: rooms, staff and bookings.
 *
 * These are the shapes the management app reads and the Cloud Functions write.
 * They live beside the call types (not inside them) because the call path must
 * keep working unchanged whether or not a hotel uses room management.
 *
 * Field names are camelCase throughout — that is a hard requirement of the
 * hotel data model, and the rules tests assert it.
 */

/* -------------------------------------------------------------------------- */
/* Rooms                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Room lifecycle:
 *
 *   ReadyToOccupy --check-in--> Occupied --check-out--> Unclean
 *        ^                                                 |
 *        +--------------- housekeeping ---------------------+
 *
 * `OutofService` is reachable from any state and is not part of the cycle.
 *
 * These exact strings are stored in Firestore and rendered in the UI — do not
 * "normalise" the casing. The legacy `ROOM_STATUS` in `types/hotels.ts`
 * (OCCUPIED / VACANT / OUT_OF_SERVICE) predates this and still appears on
 * seeded rooms; `isRoomCallable` reads whichever field a room carries.
 */
export const HOTEL_ROOM_STATUS = {
  OCCUPIED: 'Occupied',
  READY_TO_OCCUPY: 'ReadyToOccupy',
  UNCLEAN: 'Unclean',
  OUT_OF_SERVICE: 'OutofService',
} as const;

export type HotelRoomStatus =
  (typeof HOTEL_ROOM_STATUS)[keyof typeof HOTEL_ROOM_STATUS];

export const HOTEL_ROOM_STATUSES: readonly HotelRoomStatus[] = [
  HOTEL_ROOM_STATUS.READY_TO_OCCUPY,
  HOTEL_ROOM_STATUS.OCCUPIED,
  HOTEL_ROOM_STATUS.UNCLEAN,
  HOTEL_ROOM_STATUS.OUT_OF_SERVICE,
];

export function isHotelRoomStatus(value: unknown): value is HotelRoomStatus {
  return HOTEL_ROOM_STATUSES.includes(value as HotelRoomStatus);
}

/**
 * A room may only be deleted when nobody is in it and nothing is pending —
 * i.e. never while it is Occupied or waiting to be cleaned.
 */
export const DELETABLE_ROOM_STATUSES: readonly HotelRoomStatus[] = [
  HOTEL_ROOM_STATUS.READY_TO_OCCUPY,
  HOTEL_ROOM_STATUS.OUT_OF_SERVICE,
];

export function isRoomDeletable(status: unknown): boolean {
  return DELETABLE_ROOM_STATUSES.includes(status as HotelRoomStatus);
}

/**
 * Statuses a person may **set** by hand, on the Add Room and Edit Room forms.
 *
 * `Occupied` is absent, and that is the point: the only way into it is
 * `checkIn`, and the only way out of it is `checkOut`, which locates the
 * room's `checkedIn` booking and fails without one. A room typed straight to
 * Occupied would therefore have no booking to check out and — with the lock
 * below — no way back either, so it could never be sold again.
 */
export const ASSIGNABLE_ROOM_STATUSES: readonly HotelRoomStatus[] = [
  HOTEL_ROOM_STATUS.READY_TO_OCCUPY,
  HOTEL_ROOM_STATUS.UNCLEAN,
  HOTEL_ROOM_STATUS.OUT_OF_SERVICE,
];

export function isRoomStatusAssignable(status: unknown): boolean {
  return ASSIGNABLE_ROOM_STATUSES.includes(status as HotelRoomStatus);
}

/**
 * Statuses a room's status may be changed **from** by hand.
 *
 * Occupied and Unclean are not typed in, they are arrived at: `checkIn` sets
 * Occupied, `checkOut` sets Unclean and raises the room-cleaning request, and
 * completing that request sets ReadyToOccupy again. Letting the Edit Room form
 * overwrite either would cut the room loose from that workflow — a room typed
 * back to ReadyToOccupy still has a `checkedIn` booking pointing at it and its
 * adults still counted in `liveGuests`, and a room typed out of Unclean leaves
 * housekeeping holding an open request for a room nobody will clean.
 *
 * So the picker is disabled while the room is in the workflow's hands, and
 * `updateRoom` refuses the write for the same reason — the screen's view of the
 * status can be stale by the time it saves.
 */
export const EDITABLE_ROOM_STATUSES: readonly HotelRoomStatus[] = [
  HOTEL_ROOM_STATUS.READY_TO_OCCUPY,
  HOTEL_ROOM_STATUS.OUT_OF_SERVICE,
];

export function isRoomStatusEditable(status: unknown): boolean {
  return EDITABLE_ROOM_STATUSES.includes(status as HotelRoomStatus);
}

/**
 * A stored media reference.
 *
 * **The path only — never a download URL.** Two reasons, and the second is the
 * one that matters:
 *
 *  1. A Firebase download URL embeds the bucket name, so every stored URL dies
 *     the day the project or bucket changes. A path survives a migration.
 *
 *  2. A download URL carries `?alt=media&token=…`, and that token is a *bearer
 *     credential that bypasses Storage Security Rules*. Anyone who obtains the
 *     URL can fetch the object forever, whoever they are. Persisting one into
 *     Firestore therefore hands out permanent, unauthenticated access to the
 *     file to every reader of the document — and for a guest's Aadhaar or
 *     passport scan that defeats the whole point of `storage.rules`.
 *
 * Clients resolve a path to a URL at render time with `getDownloadURL()`, which
 * *is* an authenticated call subject to the read rules. So the set of people who
 * can obtain a URL stays exactly the set the rules allow.
 */
export interface MediaRef {
  /** Storage object path, e.g. `hotels/{hotelId}/rooms/{roomId}/173….webp`. */
  path: string;
  createdAt?: unknown;
}

/** `hotels/{hotelId}/rooms/{roomId}` */
export interface HotelRoomDoc {
  roomId: string;
  hotelId: string;
  /** Free-form and unique within the hotel: "101", "101A", "A-205", "Suite-01". */
  roomNumber: string;
  /** Normalised (lowercased, trimmed) copy used to enforce uniqueness. */
  roomNumberKey: string;
  roomType: string;
  roomStatus: HotelRoomStatus;
  roomInfo?: string;
  roomPhotos: MediaRef[];
  roomVideos: MediaRef[];
  createdAt?: unknown;
  updatedAt?: unknown;
}

/* -------------------------------------------------------------------------- */
/* Staff                                                                      */
/* -------------------------------------------------------------------------- */

export const STAFF_TYPE = {
  RECEPTIONIST: 'Receptionist',
  HOUSEKEEPING: 'Housekeeping',
} as const;

export type StaffType = (typeof STAFF_TYPE)[keyof typeof STAFF_TYPE];

export const STAFF_TYPES: readonly StaffType[] = [
  STAFF_TYPE.RECEPTIONIST,
  STAFF_TYPE.HOUSEKEEPING,
];

export function isStaffType(value: unknown): value is StaffType {
  return STAFF_TYPES.includes(value as StaffType);
}

/**
 * `hotels/{hotelId}/staff/{staffId}` — doc id is the staff member's Auth UID,
 * so signing in with their phone number resolves to exactly this row.
 *
 * A Receptionist ALSO gets a `hotels/{hotelId}/receptionists/{uid}` document.
 * That collection belongs to the call system and predates staff management;
 * keeping both is what lets a receptionist take guest calls without the call
 * path knowing that staff management exists.
 */
export interface StaffDoc {
  staffId: string;
  hotelId: string;
  name: string;
  /** E.164, always +91XXXXXXXXXX. */
  phone: string;
  staffType: StaffType;
  active: boolean;
  createdAt?: unknown;
  updatedAt?: unknown;
}

/* -------------------------------------------------------------------------- */
/* Bookings                                                                   */
/* -------------------------------------------------------------------------- */

export const BOOKING_STATUS = {
  CHECKED_IN: 'checkedIn',
  CHECKED_OUT: 'checkedOut',
} as const;

export type BookingStatus = (typeof BOOKING_STATUS)[keyof typeof BOOKING_STATUS];

export const GUEST_TYPE = {
  ADULT: 'adult',
  CHILD: 'child',
} as const;

export type GuestType = (typeof GUEST_TYPE)[keyof typeof GUEST_TYPE];

/**
 * Identity documents a hotel may record. Deliberately a short fixed list: the
 * check-in form offers only these, so no free-text field can invite a receptionist
 * to photograph something the workflow does not need.
 */
export const GUEST_DOCUMENT_TYPES = ['Aadhaar', 'Driving license', 'Passport'] as const;

export type GuestDocumentType = (typeof GUEST_DOCUMENT_TYPES)[number];

/** At most four document images per guest (§25). */
export const MAX_GUEST_DOCUMENTS = 4;

/**
 * At most four photos per room.
 *
 * Imported by both the Add/Edit Room form and `createRoom`/`updateRoom`, so the
 * number the screen stops offering at is literally the number the callable
 * refuses past — the two cannot drift into a form that lets you attach a fifth
 * photo and then fails on save.
 */
export const MAX_ROOM_PHOTOS = 4;

export interface GuestDocumentRef extends MediaRef {
  documentType: GuestDocumentType;
}

export interface GuestAddress {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  pincode?: string;
  country?: string;
}

/**
 * One adult as the check-in form submits them. This is the *input* shape:
 * `checkIn` parses it and turns each person into a `BookingGuestEntry` —
 * filing them in the register when they gave a phone, keeping the details on
 * the booking when they did not.
 *
 * Null for an adult after the first, whose name the front desk did not take.
 * Only the lead guest's name is mandatory — see `parseGuests` in
 * `functions/src/hotel/checkIn.ts` for why a second name is worth having but
 * not worth blocking a check-in over.
 */
export interface CheckInGuest {
  name: string | null;
  phone: string | null;
  gender: string | null;
  age: number | null;
  address: GuestAddress;
  documents: GuestDocumentRef[];
}

/**
 * One adult on a booking. Two shapes, decided by whether they gave a phone:
 *
 *  - **With a phone** — a pointer into the guest register, and nothing else.
 *    The details have one home (`GuestDoc`), so a guest's address is the same
 *    wherever it is read, and a register row refreshed on their next visit is
 *    what every past booking now points at too.
 *  - **Without a phone** — the details are kept here, on the booking. There
 *    is nothing to key a register row on and no later visit could ever be
 *    matched to it, so a row would only be a copy of this entry with a random
 *    id; the booking is the one place this person was recorded, and it holds
 *    the record.
 *
 * `guestId` is the discriminator: a string for the first shape, null for the
 * second. An adult with no details at all gets no entry, only a place in the
 * headcount.
 */
export type BookingGuestEntry =
  | { guestId: string }
  | {
      guestId: null;
      name: string | null;
      gender: string | null;
      age: number | null;
      address: GuestAddress;
      documents: GuestDocumentRef[];
    };

/**
 * `hotels/{hotelId}/bookings/{bookingId}`
 *
 * A booking is the stay — room, dates, headcount — and `guests`, one
 * `BookingGuestEntry` per recorded adult, lead guest first.
 *
 * `leadGuestName` is denormalised for the bookings list the way `roomNumber`
 * is: a row saying "Room 205 · Asha" must not cost a second read per row.
 * `guests[0]` remains the relationship.
 */
export interface BookingDoc {
  bookingId: string;
  hotelId: string;
  /** The relationship. `roomNumber` is denormalised for display only. */
  roomId: string;
  roomNumber: string;
  status: BookingStatus;
  checkinAt?: unknown;
  /** Expected checkout, captured at check-in. */
  checkoutDueAt?: unknown;
  checkoutAt?: unknown;
  adultCount: number;
  childCount: number;
  /** Lead guest first. Shorter than `adultCount` when adults were left blank. */
  guests: BookingGuestEntry[];
  /** Display only; see the note above. */
  leadGuestName: string;
  createdBy?: string | null;
  createdAt?: unknown;
  updatedAt?: unknown;
}

/* -------------------------------------------------------------------------- */
/* Guest register                                                             */
/* -------------------------------------------------------------------------- */

/**
 * `hotels/{hotelId}/guests/{guestId}` — the guest register. One row per person
 * the hotel has checked in, written by `checkIn` and read by the check-in form
 * (to fill a returning guest's details), the Guests screen, and the guest rows
 * on a booking. It is the only place a guest's details live: a booking holds
 * ids into this collection, never a copy.
 *
 * **A guest with a phone number is keyed on it** — `guestId` is the E.164
 * digits, from `guestIdForPhone`. That is the whole design:
 *
 *  - A returning guest is found by one `get` on a known id the moment their
 *    number is typed — no query, no composite index, and the rule that gates
 *    it is a plain `read`.
 *  - `checkIn` upserts the row inside its transaction with `tx.get(ref)` on a
 *    deterministic path. A query inside a transaction cannot lock a document
 *    that does not exist yet, so a random id plus "find by phone" would let two
 *    simultaneous check-ins of the same number create two register entries. A
 *    derived id makes the duplicate impossible rather than unlikely.
 *
 * The cost is that the phone *is* the identity: a guest who changes number is
 * a new row. For a hotel register that is the right trade — the number is what
 * the desk has, and what WhatsApp is keyed on.
 *
 * **A guest without a phone is not on the register.** Only the lead guest must
 * give a number; a second adult may be recorded by name alone, and that record
 * stays on the booking (`BookingGuestEntry`) — there is nothing to key a row
 * on and no later visit could ever be matched to it. So every row here has a
 * phone, and every row can be found by it.
 *
 * `documents` are references into the booking folder they were uploaded under
 * (`hotels/{hotelId}/bookings/{ref}/documents/…`); the register never copies a
 * file, only points at it, so the Storage rule protecting identity documents
 * keeps applying unchanged.
 */
export interface GuestDoc {
  guestId: string;
  hotelId: string;
  name: string | null;
  /** E.164, e.g. `+919876543210`. Always present — it is the row's identity. */
  phone: string;
  gender?: string | null;
  age?: number | null;
  address: GuestAddress;
  /** The most recent set of identity documents attached for this guest. */
  documents: GuestDocumentRef[];
  /** Bookings this guest has appeared on. */
  stayCount: number;
  firstStayAt?: unknown;
  lastStayAt?: unknown;
  lastBookingId?: string | null;
  lastRoomNumber?: string | null;
  createdAt?: unknown;
  updatedAt?: unknown;
}

/**
 * Normalises a phone number as a receptionist would type it into E.164, or
 * returns null when it is not (yet) a complete number.
 *
 * India is the default country, matching the +91 login screen and staff
 * numbers: ten digits become `+91` + digits, a leading trunk `0` is dropped,
 * and a bare `91` + ten digits is accepted as already carrying the code. Any
 * other number must be typed with its `+` and country code.
 *
 * Shared by the app (to decide when a typed number is complete enough to look
 * up) and by `checkIn` (to key the register row), so the two can never
 * disagree about which document a number maps to.
 */
export function normalizeGuestPhone(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.replace(/[\s()-]/g, '');
  const explicit = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');

  if (explicit) {
    // Full international form: 8–15 digits after the plus (E.164's ceiling).
    return /^[1-9]\d{7,14}$/.test(digits) ? `+${digits}` : null;
  }
  if (/^[6-9]\d{9}$/.test(digits)) return `+91${digits}`;
  if (/^0[6-9]\d{9}$/.test(digits)) return `+91${digits.slice(1)}`;
  if (/^91[6-9]\d{9}$/.test(digits)) return `+${digits}`;
  return null;
}

/** The register document id for a phone: its E.164 digits without the `+`. */
export function guestIdForPhone(phone: string): string {
  return phone.replace(/\D/g, '');
}
